import React, { lazy, Suspense, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import AppLayout from '../shared/AppLayout';
import ChangePassword from './ChangePassword';
import TelegramLinking from './TelegramLinking';
import { useDesktopView } from '../../hooks/useDesktopView';
import ExportPrivateKeyModal from '../Wallet/ExportPrivateKeyModal';

// Lazy load desktop component
const DesktopSettingsPage = lazy(() => import('./desktop/DesktopSettingsPage'));

// Loading fallback for desktop
const DesktopLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Mobile Settings Page Component
const MobileSettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [showExportModal, setShowExportModal] = useState(false);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  return (
    <AppLayout title="Settings">
      {/* Account Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 rounded-2xl p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-white mb-4">Account</h2>

        {user?.wallet_address && (
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-1">Wallet Address</p>
            <p className="text-gray-300 text-sm break-all font-mono">
              {user.wallet_address}
            </p>
          </div>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full py-3 bg-red-500/10 hover:bg-red-500/20
                   border border-red-500/30 text-red-400 font-semibold
                   rounded-xl transition-colors"
        >
          Log Out
        </motion.button>
      </motion.div>

      {/* Change Password */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <ChangePassword />
      </motion.div>

      {/* Telegram Notifications */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-6"
      >
        <TelegramLinking compact />
      </motion.div>

      {/* Danger Zone - Export Private Key */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gray-900 rounded-2xl p-6 border border-red-500/20"
      >
        <h2 className="text-lg font-semibold text-white mb-2 flex items-center">
          <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Danger Zone
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          Export your wallet's private key for backup or migration purposes. Only do this if you understand the security risks.
        </p>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowExportModal(true)}
          className="w-full py-3 bg-yellow-500/10 hover:bg-yellow-500/20
                   border border-yellow-500/30 text-yellow-400 font-semibold
                   rounded-xl transition-colors flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          Export Private Key
        </motion.button>
      </motion.div>

      {/* Export Private Key Modal */}
      {showExportModal && (
        <ExportPrivateKeyModal onClose={() => setShowExportModal(false)} />
      )}
    </AppLayout>
  );
};

// Main SettingsPage component with responsive switching
const SettingsPage: React.FC = () => {
  const { isDesktop } = useDesktopView();

  if (isDesktop) {
    return (
      <Suspense fallback={<DesktopLoadingFallback />}>
        <DesktopSettingsPage />
      </Suspense>
    );
  }

  return <MobileSettingsPage />;
};

export default SettingsPage;
